// A card is a point in a cube. Each ring of its glyph is one direction, and
// each ring reads down (-1), level (0) or up (+1). A folded ring stands for
// all three values at once. A deck has one, two or three rings: 3, 9 or 27
// cards. Rings are listed inner ring first.

export type Trit = -1 | 0 | 1
export type Ring = Trit | "folded"
export type Rings = readonly [Ring] | readonly [Ring, Ring] | readonly [Ring, Ring, Ring]
export type Width = Rings["length"]
export type RingIndex = 0 | 1 | 2

// A glyph written as text, inner ring first: "+0-" is up, level, down; "*"
// is a folded ring. Saves and the Codex use these as names.
export type Sym = "-" | "0" | "+" | "*"
export type GlyphKey = Sym | `${Sym}${Sym}` | `${Sym}${Sym}${Sym}`

export const WIDTHS = [1, 2, 3] as const satisfies readonly Width[]
const EVERY_RING = [-1, 0, 1, "folded"] as const satisfies readonly Ring[]

// The two laws a single ring keeps; every other law is built on them. Both
// are locked by tests/types/laws.ts. A folded ring neither rises nor turns.
// A ring raised one notch: down to level, level to up; up is the top.
export const RAISED = { [-1]: 0, 0: 1, 1: "top" } as const satisfies Record<Trit, Trit | "top">
// A ring as a mirror shows it: down and up change places; level stays.
export const TURNED = { [-1]: 1, 0: 0, 1: -1 } as const satisfies Record<Trit, Trit>

export const isFolded = (ring: Ring): boolean => ring === "folded"

export const symOf = (ring: Ring): Sym => {
  switch (ring) {
    case -1:
      return "-"
    case 0:
      return "0"
    case 1:
      return "+"
    case "folded":
      return "*"
  }
}

// A key only ever holds the four symbols; anything else reads as level.
const ringOfChar = (c: string): Ring => (c === "+" ? 1 : c === "-" ? -1 : c === "*" ? "folded" : 0)

export const keyOf = (rings: Rings): GlyphKey => {
  switch (rings.length) {
    case 1:
      return symOf(rings[0])
    case 2:
      return `${symOf(rings[0])}${symOf(rings[1])}`
    case 3:
      return `${symOf(rings[0])}${symOf(rings[1])}${symOf(rings[2])}`
  }
}

export const ringsOf = (key: GlyphKey): Rings => {
  const [a = 0, b, c] = key.split("").map(ringOfChar)
  if (b === undefined) return [a]
  if (c === undefined) return [a, b]
  return [a, b, c]
}

// The same change applied to each ring, keeping the width.
export const mapRings = (rings: Rings, f: (ring: Ring, i: RingIndex) => Ring): Rings => {
  switch (rings.length) {
    case 1:
      return [f(rings[0], 0)]
    case 2:
      return [f(rings[0], 0), f(rings[1], 1)]
    case 3:
      return [f(rings[0], 0), f(rings[1], 1), f(rings[2], 2)]
  }
}

export const ringIndexes = (rings: Rings): readonly RingIndex[] => {
  switch (rings.length) {
    case 1:
      return [0]
    case 2:
      return [0, 1]
    case 3:
      return [0, 1, 2]
  }
}

export const ringAt = (rings: Rings, i: RingIndex): Ring | undefined => {
  const list: readonly Ring[] = rings
  return list[i]
}

export const withRing = (rings: Rings, at: RingIndex, ring: Ring): Rings =>
  mapRings(rings, (r, i) => (i === at ? ring : r))

const negateRing = (ring: Ring): Ring => (ring === "folded" ? ring : TURNED[ring])

// What a card shows in a mirror: every open ring turned over.
export const negate = (rings: Rings): Rings => mapRings(rings, negateRing)

const ringValue = (ring: Ring): number => (ring === "folded" ? 0 : ring)

// The number a card spells, folded rings read as level. Used for pitch.
export const valueOf = (rings: readonly Ring[]): number => rings.reduceRight((n: number, r) => n * 3 + ringValue(r), 0)

// Scale 1 is a single point. Each folded ring triples what a card covers.
export const scaleOf = (rings: readonly Ring[]): number => 1 + rings.filter(isFolded).length

export const maxValue = (width: number): number => (3 ** width - 1) / 2

const tritOfRemainder = (r: number): Trit => (r === 1 ? 1 : r === 2 ? -1 : 0)

// A number written in balanced ternary with `count` digits, least
// significant first. Used for the alien numerals and for card values.
export const digits = (n: number, count: number): readonly Trit[] => {
  const out: Trit[] = []
  let x = n
  for (let i = 0; i < count; i++) {
    const t = tritOfRemainder(((x % 3) + 3) % 3)
    out.push(t)
    x = (x - t) / 3
  }
  if (x !== 0) throw new RangeError(`${n} does not fit in ${count} digits`)
  return out
}

export const pointOf = (value: number, width: Width): Rings => {
  const [a = 0, b = 0, c = 0] = digits(value, width)
  switch (width) {
    case 1:
      return [a]
    case 2:
      return [a, b]
    case 3:
      return [a, b, c]
  }
}

// The whole cube: every point of `width` rings, lowest value first.
export const fullDeck = (width: Width): readonly Rings[] => {
  const m = maxValue(width)
  return Array.from({ length: 2 * m + 1 }, (_, k) => pointOf(k - m, width))
}

const pairsWith = (a: Ring): readonly (readonly [Ring, Ring])[] => EVERY_RING.map((b) => [a, b] as const)
const triplesOf = (a: Ring, b: Ring): readonly (readonly [Ring, Ring, Ring])[] => EVERY_RING.map((c) => [a, b, c] as const)
const triplesWith = (a: Ring): readonly (readonly [Ring, Ring, Ring])[] => EVERY_RING.flatMap((b) => triplesOf(a, b))

// Every glyph of a width, folded rings included: 4, 16 or 64.
export const allGlyphs = (width: Width): readonly Rings[] => {
  switch (width) {
    case 1:
      return EVERY_RING.map((a) => [a] as const)
    case 2:
      return EVERY_RING.flatMap(pairsWith)
    case 3:
      return EVERY_RING.flatMap(triplesWith)
  }
}

// Every glyph name there is, for reading a name back from the page.
export const ALL_KEYS: readonly GlyphKey[] = WIDTHS.flatMap((w) => allGlyphs(w).map(keyOf))

export const glyphKeyOf = (s: string): GlyphKey | undefined => ALL_KEYS.find((k) => k === s)
